/* eslint-disable import/prefer-default-export */
import useMobxStores from 'hooks/useMobxStores';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import CatalogStore from 'stores/Catalog';
import type { CatalogEntry } from 'types';
import { LiveVirtualClass } from 'types/liveClass';

interface VLCGroups {
  /**
   * List of VLCs the user is currently enrolled in that are ongoing.
   */
  ongoingEnrolledVLC: LiveVirtualClass[];
  /**
   * List of VLCs the user is enrolled in that they have already attended (completed).
   */
  attentedEnrolledVLC: LiveVirtualClass[];
  /**
   * List of VLCs the user is enrolled in that are scheduled for the future.
   */
  upcomingEnrolledVLC: LiveVirtualClass[];
}

/**
 * Represents the user-facing status of a course based on the user's interaction with Virtual Learning Classes (VLCs).
 *
 * - `Unavailable`: The course has no VLCs available for enrollment for this user.
 * - `Available`: The course has VLCs available for enrollment for this user.
 * - `Enrolled`: The user is enrolled in at least one VLC for this course.
 * - `Ongoing`: The user is enrolled in at least one ongoing VLC for this course.
 * - `Attended`: The user has attended (completed) at least one VLC for this course.
 */
export enum CourseLvcUserFacingStatus {
  Unavailable = 'unavailable',
  Available = 'available',
  Enrolled = 'enrolled',
  Ongoing = 'ongoing',
  Attended = 'attended',
}

/**
 * Categorize VLCs into ongoing, attended, and upcoming based on current time.
 * @param enrolledVLC List of VLCs the user is enrolled in.
 * @returns An object containing arrays of VLCs categorized by their status.
 */
const categorizeVLCs = (enrolledVLC: LiveVirtualClass[]): VLCGroups => {
  const now = DateTime.now();
  const sortedVLCs = [...enrolledVLC].sort(
    (a, b) =>
      DateTime.fromISO(a.end_time).toMillis() -
      DateTime.fromISO(b.end_time).toMillis(),
  );

  return sortedVLCs.reduce(
    (groups, vlc) => {
      const startTime = DateTime.fromISO(vlc.start_time);
      const endTime = DateTime.fromISO(vlc.end_time);

      if (startTime > now) {
        groups.upcomingEnrolledVLC.push(vlc);
      } else if (endTime <= now) {
        groups.attentedEnrolledVLC.push(vlc);
      } else {
        groups.ongoingEnrolledVLC.push(vlc);
      }

      return groups;
    },
    {
      ongoingEnrolledVLC: [],
      attentedEnrolledVLC: [],
      upcomingEnrolledVLC: [],
    },
  );
};

const getCourseVlcUserFacingStatus = (
  hasAvailableVLC: boolean,
  vlcGroups: VLCGroups,
) => {
  const { ongoingEnrolledVLC, attentedEnrolledVLC, upcomingEnrolledVLC } =
    vlcGroups;

  // priority order: ongoing > enrolled (upcoming) > attended > available > unavailable
  if (ongoingEnrolledVLC.length > 0) {
    return CourseLvcUserFacingStatus.Ongoing;
  }
  if (upcomingEnrolledVLC.length > 0) {
    return CourseLvcUserFacingStatus.Enrolled;
  }
  if (attentedEnrolledVLC.length > 0) {
    return CourseLvcUserFacingStatus.Attended;
  }
  if (hasAvailableVLC) {
    return CourseLvcUserFacingStatus.Available;
  }
  return CourseLvcUserFacingStatus.Unavailable;
};

export type EntryVlcInfo = {
  /**
   * Indicates if there are available VLCs for enrollment for this course.
   */
  hasAvailableVLC: boolean;
  /**
   * List of available VLCs for enrollment for this course.
   */
  availableVLC: LiveVirtualClass[];
  /**
   * Indicates if the user is enrolled in any VLCs for this course, independent of their status (ongoing, attended, upcoming).
   */
  hasEnrolledVLC: boolean;
  /**
   * The human readable status of the course based on VLC enrollment and availability.
   */
  courseVlcUserFacingStatus: CourseLvcUserFacingStatus;
} & VLCGroups;

type LvcMapsByCode = {
  enrolledLvcByCode: Map<string, LiveVirtualClass[]>;
  upcomingLvcByCode: Map<string, LiveVirtualClass[]>;
};

/**
 * Builds VLC-related information for a single catalog entry.
 * Returns undefined when the entry is not a regular course or has no code.
 */
export const getEntryVlcInfo = (
  entry: CatalogEntry,
  { enrolledLvcByCode, upcomingLvcByCode }: LvcMapsByCode,
): EntryVlcInfo | undefined => {
  if (
    !CatalogStore.isRegularCourse(entry.course_type) ||
    typeof entry.code !== 'string'
  ) {
    return undefined;
  }

  const availableVLC = upcomingLvcByCode.get(entry.code) || [];
  const hasAvailableVLC = availableVLC.length > 0;
  const enrolledVLC = enrolledLvcByCode.get(entry.code) || [];
  const hasEnrolledVLC = enrolledVLC.length > 0;
  const categorizedVLCs = categorizeVLCs(enrolledVLC);
  const courseVlcUserFacingStatus = getCourseVlcUserFacingStatus(
    hasAvailableVLC,
    categorizedVLCs,
  );

  return {
    ...categorizedVLCs,
    hasEnrolledVLC,
    hasAvailableVLC,
    availableVLC,
    courseVlcUserFacingStatus,
  };
};

/**
 * Returns VLC-related information for a single catalog entry.
 */
export const useEntryLiveClassInfo = (entry?: CatalogEntry | null) => {
  const { liveClassStore } = useMobxStores();
  const { enrolledLvcByCode, upcomingLvcByCode } = liveClassStore;

  return useMemo(() => {
    if (!entry) {
      return undefined;
    }

    return getEntryVlcInfo(entry, {
      enrolledLvcByCode,
      upcomingLvcByCode,
    });
  }, [entry, enrolledLvcByCode, upcomingLvcByCode]);
};

/**
 * Custom hook to get VLC-related information for each course in the catalog.
 * @param entriesOverride - Optional list of entries to use instead of catalog paginatedEntries (e.g. learning path offerings).
 * @returns A map where the key is the course code and the value contains VLC-related information for that course.
 */
export const useCourseLiveClassInfo = (
  entriesOverride?: CatalogEntry[] | null,
) => {
  const { catalogStore, liveClassStore } = useMobxStores();
  const { paginatedEntries } = catalogStore;
  const {
    enrolledLvcByCode: enrolledVLCByCode,
    upcomingLvcByCode: upcomingVLCByCode,
  } = liveClassStore;

  const entries =
    entriesOverride !== undefined && entriesOverride !== null
      ? entriesOverride
      : paginatedEntries;

  const vlcInfoByEntry = useMemo(() => {
    const infoMap = new Map<string, EntryVlcInfo>();

    entries.forEach((entry) => {
      const entryVlcInfo = getEntryVlcInfo(entry, {
        enrolledLvcByCode: enrolledVLCByCode,
        upcomingLvcByCode: upcomingVLCByCode,
      });

      if (entryVlcInfo && typeof entry.code === 'string') {
        infoMap.set(entry.code, entryVlcInfo);
      }
    });

    return infoMap;
  }, [entries, enrolledVLCByCode, upcomingVLCByCode]);

  return vlcInfoByEntry;
};
